import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Copy, Check, FileDown } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import API_BASE_URL, { FRONTEND_URL } from '../config/api';
import { generateBubbleForm } from '../utils/bubbleFormGenerator';

interface Question {
  id: number;
  question_number: number;
  number_of_answers: number;
  part_id: number | null;
}

interface Part {
  id: number;
  titre: string;
  ordre: number;
}

interface Test {
  id: number;
  title: string;
  total_questions: number;
  statut_libelle: string;
  share_code?: string;
  questions?: Question[];
  parts?: Part[];
}

const ShareTest: React.FC = () => {
  const navigate = useNavigate();
  const { testId } = useParams<{ testId: string }>();
  const { t } = useTranslation();
  const [test, setTest] = useState<Test | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [copied, setCopied] = useState<boolean>(false);
  const [regenerating, setRegenerating] = useState<boolean>(false);
  const [shareCodeCopied, setShareCodeCopied] = useState<boolean>(false);

  // Générer l'URL complète pour le test étudiant depuis la variable d'environnement
  const studentTestUrl = `${FRONTEND_URL}/student-answer-test/${testId}`;

  useEffect(() => {
    fetchTest();
  }, [testId]);

  const fetchTest = async (): Promise<void> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/${testId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(t('tests.share.fetchError'));
      }

      const data = await response.json();
      // L'API retourne test avec questions et parts
      setTest({
        ...data.test,
        questions: data.test.questions || [],
        parts: data.test.parts || []
      });
      setLoading(false);

    } catch (error) {
      console.error('Erreur:', error);
      navigate('/my-tests');
    }
  };

  const handleCopyLink = async (): Promise<void> => {
    try {
      // Méthode moderne avec Clipboard API
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(studentTestUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        // Fallback pour les navigateurs qui ne supportent pas l'API Clipboard
        // ou pour les connexions non-sécurisées
        const textArea = document.createElement('textarea');
        textArea.value = studentTestUrl;

        // Rendre le textarea invisible
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);

        // Sélectionner et copier
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand('copy');
          if (successful) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          } else {
            throw new Error(t('tests.share.copyError'));
          }
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la copie:', error);
    }
  };

  const handleBack = (): void => {
    navigate('/my-tests');
  };

  const handleCopyCode = async (): Promise<void> => {
    if (!test?.share_code) return;

    try {
      await navigator.clipboard.writeText(test.share_code);
      setShareCodeCopied(true);
      setTimeout(() => setShareCodeCopied(false), 2000);
    } catch (error) {
      console.error('Erreur lors de la copie du code:', error);
    }
  };

  const handleRegenerateCode = async (): Promise<void> => {
    const confirmed = window.confirm(
      'Êtes-vous sûr de vouloir régénérer le code de partage ? L\'ancien code ne fonctionnera plus.'
    );

    if (!confirmed) return;

    setRegenerating(true);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${API_BASE_URL}/api/tests/${testId}/regenerate-code`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Échec de la régénération du code');
      }

      const data = await response.json();

      // Mettre à jour le test avec le nouveau code
      setTest(prev => prev ? { ...prev, share_code: data.shareCode } : null);

    } catch (error) {
      console.error('Erreur lors de la régénération du code:', error);
      alert('Erreur lors de la régénération du code');
    } finally {
      setRegenerating(false);
    }
  };

  const handleDownloadBubbleForm = async (): Promise<void> => {
    if (!test || !test.questions || test.questions.length === 0) {
      return;
    }

    try {
      await generateBubbleForm({
        id: test.id,
        title: test.title,
        total_questions: test.total_questions,
        questions: test.questions,
        parts: test.parts || []
      });
    } catch (error) {
      console.error('Erreur lors de la génération du formulaire:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
        <div className="text-white text-xl">{t('tests.share.loading')}</div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
        <div className="text-white text-xl">{t('tests.share.testNotFound')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="text-gray-600 hover:text-gray-800 transition"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-2xl font-bold text-gray-800">{t('tests.share.title')}</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-2xl p-8">
          {/* Titre du test */}
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">
              {test.title}
            </h2>
            <p className="text-gray-600">
              {test.total_questions} {t('tests.share.questions')} • {t('tests.share.status')}: {test.statut_libelle}
            </p>
          </div>

          {/* Section Code de partage */}
          <div className="mb-10">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Code de partage
            </h3>
            <p className="text-gray-600 mb-4">
              Les étudiants peuvent entrer ce code sur <strong>{FRONTEND_URL}</strong>
            </p>

            <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
              <div className="flex-1 bg-gradient-to-r from-blue-100 to-blue-50 rounded-lg p-6 border-2 border-blue-300">
                <p className="text-4xl md:text-5xl font-bold text-blue-800 text-center tracking-widest font-mono">
                  {test.share_code || 'N/A'}
                </p>
              </div>

              <div className="flex md:flex-col gap-2">
                <button
                  onClick={handleCopyCode}
                  className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold transition ${
                    shareCodeCopied
                      ? 'bg-green-600 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {shareCodeCopied ? (
                    <>
                      <Check className="w-5 h-5" />
                      Copié
                    </>
                  ) : (
                    <>
                      <Copy className="w-5 h-5" />
                      Copier
                    </>
                  )}
                </button>

                <button
                  onClick={handleRegenerateCode}
                  disabled={regenerating}
                  className="flex-1 md:flex-none px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {regenerating ? 'Régénération...' : 'Régénérer'}
                </button>
              </div>
            </div>
          </div>

          {/* Section Lien */}
          <div className="mb-10">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              {t('tests.share.linkTitle')}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('tests.share.linkDescription')}
            </p>

            <div className="flex gap-3">
              <div className="flex-1 bg-gray-100 rounded-lg p-4 border border-gray-300">
                <p className="text-gray-800 break-all font-mono text-sm">
                  {studentTestUrl}
                </p>
              </div>
              <button
                onClick={handleCopyLink}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition ${
                  copied
                    ? 'bg-green-600 text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {copied ? (
                  <>
                    <Check className="w-5 h-5" />
                    {t('tests.share.copiedButton')}
                  </>
                ) : (
                  <>
                    <Copy className="w-5 h-5" />
                    {t('tests.share.copyButton')}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Section Formulaire à bulles */}
          <div className="mb-10">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              {t('tests.share.bubbleFormTitle') || 'Formulaire à bulles'}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('tests.share.bubbleFormDescription') || 'Téléchargez un formulaire de réponses à bulles que vos étudiants pourront remplir manuellement.'}
            </p>

            <button
              onClick={handleDownloadBubbleForm}
              className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition"
            >
              <FileDown className="w-5 h-5" />
              {t('tests.share.downloadBubbleForm') || 'Télécharger le formulaire PDF'}
            </button>
          </div>

          {/* Section QR Code */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              {t('tests.share.qrCodeTitle')}
            </h3>
            <p className="text-gray-600 mb-6">
              {t('tests.share.qrCodeDescription')}
            </p>

            <div className="flex justify-center">
              <div className="bg-white p-8 rounded-lg border-4 border-gray-300 shadow-lg">
                <QRCodeSVG
                  value={studentTestUrl}
                  size={256}
                  level="H"
                  includeMargin={true}
                />
              </div>
            </div>

            <div className="text-center mt-6">
              <p className="text-sm text-gray-500">
                {t('tests.share.qrCodeScreenshotTip')}
              </p>
            </div>
          </div>

          {/* Informations supplémentaires */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
            <h4 className="font-bold text-gray-800 mb-2">
              ℹ️ {t('tests.share.instructions')}
            </h4>
            <ul className="text-gray-700 space-y-2 text-sm">
              {(t('tests.share.instructionsList', { returnObjects: true }) as string[]).map((instruction, index) => (
                <li key={index}>• {instruction}</li>
              ))}
            </ul>
          </div>

          {/* Boutons de navigation */}
          <div className="flex justify-center gap-4 mt-8">
            <button
              onClick={handleBack}
              className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-8 rounded-lg transition"
            >
              {t('tests.share.backToTests')}
            </button>
            <button
              onClick={() => navigate(`/test-results/${testId}`)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition"
            >
              {t('tests.share.viewResults')}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ShareTest;
